/**
 * Stone Investment Area Selector
 * 
 * Region/area navigation with Stone Investment's luxury aesthetic:
 * - Grid of area cards with dark map thumbnails
 * - Region grouping
 * - Elegant hover effects
 */

'use client';

import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import type { AreaRegionGroup } from '@routeroyal/theme-core';
import { MAURITIUS_AREAS } from '@routeroyal/theme-core';

import { StoneAreaMapThumbnail } from './StoneAreaMapThumbnail';

export interface StoneAreaSelectorProps {
  regions: AreaRegionGroup[];
  currentArea?: string;
  basePath?: string;
  messages?: Record<string, string>;
  locale?: string;
  className?: string;
}

export function StoneAreaSelector({
  regions,
  currentArea,
  basePath = '/areas',
  messages = {},
  locale = 'en',
  className = '',
}: StoneAreaSelectorProps) {
  return (
    <section className={`bg-white py-20 px-6 md:px-12 ${className}`}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
            {messages.exploreAreas || 'Explore Areas'}
          </p>
          <h2 
            className="font-serif text-3xl md:text-4xl text-stone-900"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {messages.otherDestinations || 'Other Destinations'}
          </h2>
        </div>
        
        {/* Regions */}
        {regions.map((region) => (
          <div key={region.region} className="mb-16 last:mb-0">
            {/* Region Name */}
            <h3 className="font-serif text-2xl text-stone-900 mb-8 italic border-b border-stone-200 pb-4">
              {region.region}
            </h3>
            
            {/* Areas Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {region.areas.map((area) => {
                const isActive = area.slug === currentArea;
                const href = `/${locale}${basePath}/${area.slug}`;
                const areaData = MAURITIUS_AREAS[area.slug];
                
                return (
                  <Link
                    key={area.slug}
                    href={href}
                    className={`group relative overflow-hidden ${
                      isActive ? 'ring-1 ring-stone-900' : ''
                    }`}
                  >
                    {/* Map Thumbnail */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      {areaData?.latitude && areaData?.longitude ? (
                        <StoneAreaMapThumbnail
                          latitude={areaData.latitude}
                          longitude={areaData.longitude}
                          zoom={12}
                          variant="light"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-stone-200 to-stone-300" />
                      )}
                      
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-colors duration-500 pointer-events-none" />
                      
                      {/* Arrow indicator */}
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                        <ArrowUpRight size={20} className="text-white" strokeWidth={1.5} />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 bg-white border-t border-stone-200">
                      <h4 className={`font-serif text-lg text-stone-900 transition-all duration-300 ${
                        isActive ? 'italic' : 'group-hover:italic'
                      }`}>
                        {area.name}
                      </h4>
                    </div>
                    
                    {/* Active Indicator */}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-stone-900" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default StoneAreaSelector;
